/*
 * @nevware21/ts-utils
 * https://github.com/nevware21/ts-utils
 *
 * Copyright (c) 2022 NevWare21 Solutions LLC
 * Licensed under the MIT license.
 */

import { fnApply, fnCall } from "../funcs/funcs";
import { ArrSlice, CALL, CONSTRUCTOR, NAME, NULL_VALUE, PROTOTYPE } from "../internal/constants";
import { objCreate } from "../object/create";
import { objDefine } from "../object/define";
import { objGetPrototypeOf } from "../object/object";
import { objSetPrototypeOf } from "../object/set_proto";
import { safe } from "./safe";

/**
 * Defines the definition of the custom error constructor
 * Used by: {@link createCustomError}
 * @group Error
 */
export interface CustomErrorConstructor<T extends Error = Error> extends ErrorConstructor {
    new(message?: string): T;
    (message?: string): T;
    readonly prototype: T;
}

/**
 * @internal
 * @ignore
 */
/*#__NO_SIDE_EFFECTS__*/
function _createCustomError<T>(name: string, d: any, b: any): T {
    safe(objDefine, [ d, NAME, { v: name, c: true, e: false }]);
    d = objSetPrototypeOf(d, b);
    function __() {
        this[CONSTRUCTOR] = d;
        safe(objDefine, [this, NAME, { v: name, c: true, e: false }]);
    }

    d[PROTOTYPE] = b === NULL_VALUE ? objCreate(b) : ((__ as any)[PROTOTYPE] = b[PROTOTYPE], new (__ as any)());

    return d;
}

function  _setName(baseClass: any, name: string) {
    name && (baseClass[NAME] = name);
    //name && (baseClass[PROTOTYPE][NAME] = name);
}

/**
 * Create a Custom Error class which may be used to throw custom errors.
 * @group Error
 * @param name - The name of the Custom Error
 * @param constructCb - [Optional] An optional callback function to call when a
 * new Custom Error instance is being created. (Since v0.16.0) The callback is invoked with `this` bound to
 * the same instance that is also passed as the `self` (1st) argument, so implementations may use either
 * `this.prop = ...` or `self.prop = ...` interchangeably - this only applies when `constructCb` is a normal
 * (non-arrow) function, as arrow functions ignore the bound `this` and keep their own lexically captured value.
 * The 3rd argument, `isOwnInstance`, is `true` when this class is the leaf-most (most-derived) type actually
 * being instantiated (eg. `new ThisClass()`), and `false` when this class's own constructor is only running
 * because it is a base class of some other custom error further down an inheritance chain (eg. `new SomeSubClass()`,
 * where `SomeSubClass` was created via `createCustomError(..., ThisClass)`). Use this to skip work - such as
 * your own stack-trace capture - that only the leaf-most instance actually needs, since a base class's own
 * construction step runs (and would otherwise redo that work) for every instance of every one of its subclasses too.
 * @param errorBase - [Optional] (since v0.9.6) The error class to extend for this class, defaults to Error.
 * @param superArgsFn - [Optional] (since v0.13.0) An optional function that receives the constructor arguments and
 * returns the arguments to pass to the base class constructor. When not provided all constructor
 * arguments are forwarded to the base class. Use this to support a different argument order or
 * to pass a subset of arguments to the base class (similar to calling `super(...)` in a class).
 * @returns A new Error `class`
 * @example
 * ```ts
 * import { createCustomError, isError } from "@nevware21/ts-utils";
 *
 * // For an error that just contains a message
 * let myCustomErrorError = createCustomError("MessageError");
 *
 * try {
 *     throw new myCustomErrorError("Error Message!");
 * } catch(e) {
 *      // e.name === MessageError
 *      // isError(e) === true;
 *      // Object.prototype.toString.call(e) === "[object Error]";
 * }
 *
 * // Or a more complex error object
 * interface MyCriticalErrorConstructor extends CustomErrorConstructor {
 *     new(message: string, file: string, line: number, col: number): MyCriticalError;
 *     (message: string, file: string, line: number, col: number): MyCriticalError;
 * }
 *
 * interface MyCriticalError extends Error {
 *     readonly errorId: number;
 *     readonly args: any[];        // Holds all of the arguments passed during construction
 * }
 *
 * let _totalErrors = 0;
 * let myCustomError = createCustomError<MyCriticalErrorConstructor>("CriticalError", (self, args) => {
 *     _totalErrors++;
 *     self.errorId = _totalErrors;
 *     self.args = args;
 * });
 *
 * try {
 *     throw new myCustomError("Not Again!");
 * } catch(e) {
 *      // e.name === CriticalError
 *      // isError(e) === true;
 *      // Object.prototype.toString.call(e) === "[object Error]";
 * }
 *
 * // ----------------------------------------------------------
 * // Extending another custom error class
 * // ----------------------------------------------------------
 *
 * let AppError = createCustomError("ApplicationError");
 * let theAppError = new appError();
 *
 * isError(theAppError);                    // true
 * theAppError instanceof Error;            // true
 * theAppError instanceof AppError;         // true
 *
 * let StartupError = createCustomError("StartupError", null, AppError);
 * let theStartupError = new StartupError();
 *
 * isError(theStartupError);                // true
 * theStartupError instanceof Error;        // true
 * theStartupError instanceof AppError;     // true
 * theStartupError instanceof StartupError; // true
 *
 * // ----------------------------------------------------------
 * // Custom error with reordered / transformed arguments
 * // (the superArgsFn maps constructor args to base class args)
 * // ----------------------------------------------------------
 *
 * interface HttpErrorConstructor extends CustomErrorConstructor<HttpError> {
 *     new(statusCode: number, message: string): HttpError;
 *     (statusCode: number, message: string): HttpError;
 * }
 *
 * interface HttpError extends Error {
 *     readonly statusCode: number;
 * }
 *
 * // HttpError takes (statusCode, message) but base Error expects (message)
 * let MyHttpError = createCustomError<HttpErrorConstructor>("HttpError",
 *     (self, args) => {
 *         self.statusCode = args[0];
 *     },
 *     Error,
 *     (args) => [ args[1] ]  // pass only the message to base Error constructor
 * );
 *
 * let err = new MyHttpError(404, "Not Found");
 * err.message;     // "Not Found"
 * err.statusCode;  // 404
 * ```
 */
/*#__NO_SIDE_EFFECTS__*/
export function createCustomError<T extends ErrorConstructor = CustomErrorConstructor, B extends ErrorConstructor = ErrorConstructor>(
    name: string,
    constructCb?: ((this: any, self: any, args: IArguments, isOwnInstance?: boolean) => void) | null,
    errorBase?: B,
    superArgsFn?: ((args: IArguments) => ArrayLike<any>) | null): T {

    let theBaseClass = errorBase || Error;
    let orgName = theBaseClass[PROTOTYPE][NAME];
    let captureFn = Error.captureStackTrace;
    let ctorFn = function (this: any) {
        let _this = this;
        let theArgs = arguments;
        // this[CONSTRUCTOR] always resolves (via the prototype chain) to the leaf-most class
        // actually being instantiated, so comparing it against ourselves tells us whether we're
        // that leaf class or just being invoked as a base class for a child further down the chain
        let isOwnInstance = _this[CONSTRUCTOR] === ctorFn;

        try {
            safe(_setName, [theBaseClass, name]);
            let _self = fnApply(theBaseClass, _this, superArgsFn ? superArgsFn(theArgs) : ArrSlice[CALL](theArgs)) || _this;
            if (_self !== _this) {
                // Looks like runtime error constructor reset the prototype chain, so restore it
                let orgProto = objGetPrototypeOf(_this);
                if (orgProto !== objGetPrototypeOf(_self)) {
                    objSetPrototypeOf(_self, orgProto);
                }
            }

            if (isOwnInstance) {
                // If this is the leaf-most class being instantiated, then capture the stack trace for it
                captureFn && captureFn(_self, ctorFn);
            }

            // Run the provided construction function
            if (constructCb) {
                // Call the callback with the leaf-most instance, the original arguments, and whether this class is
                // the leaf-most class being instantiated (or just a base class of some other custom error further
                // down the inheritance chain), so that the callback can skip work that only the leaf-most instance
                // actually needs to do.  And support construction functions that want to use `this` instead of the
                // first argument.
                fnCall(constructCb, _self, _self, theArgs, isOwnInstance);
            }

            return _self;
        } finally {
            safe(_setName, [theBaseClass, orgName]);
        }
    };

    return _createCustomError<T>(name, ctorFn, theBaseClass);
}

/**
 * @internal
 * @ignore
 */
let _unsupportedError: CustomErrorConstructor;

/**
 * Throw a custom `UnsupportedError` Error instance with the given message.
 * @group Error
 * @param message - The message to include in the exception
 * @example
 * ```ts
 * import { throwUnsupported } from "@nevware21/ts-utils";
 *
 * if (!window) {
 *     throwUnsupported("A window is needed for this operation");
 * }
 * ```
 */
export function throwUnsupported(message?: string): never {
    if (!_unsupportedError) {
        // Lazily create the class
        _unsupportedError = createCustomError("UnsupportedError");
    }

    throw new _unsupportedError(message);
}
